import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Tabs,
  Tab,
  Stack,
  Divider,
  Grid,
  Avatar,
  IconButton,
  Button,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Palette as PaletteIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../../utils/formatters.js';

const colorMap = {
  primary: '#1976d2',
  secondary: '#dc004e',
  success: '#2e7d32',
  error: '#d32f2f',
  warning: '#ed6c02',
  info: '#0288d1',
  default: '#757575',
};

function LeadStageDetail({ leadStage, onEdit, onDelete }) {
  const [tabValue, setTabValue] = React.useState(0);

  if (!leadStage) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a lead stage to view details
        </Typography>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Lead Stage Details
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={leadStage.name}
                color={leadStage.color}
                size="small"
                sx={{
                  backgroundColor: colorMap[leadStage.color] || colorMap.default,
                  color: 'white',
                  '& .MuiChip-label': {
                    color: 'white',
                  },
                }}
              />
              {leadStage.order && (
                <Chip
                  icon={<SortIcon />}
                  label={`Order: ${leadStage.order}`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => onEdit(leadStage)} color="primary">
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(leadStage)} color="error">
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>

        {leadStage.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {leadStage.description}
          </Typography>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" />
          <Tab label="Timeline" />
          <Tab label="Usage" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Stage Name
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TrendingUpIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="medium">
                        {leadStage.name}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2">
                      {leadStage.description || 'No description provided'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Color Theme
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PaletteIcon fontSize="small" color="action" />
                      <Chip
                        label={leadStage.color}
                        color={leadStage.color}
                        size="small"
                        sx={{
                          backgroundColor: colorMap[leadStage.color] || colorMap.default,
                          color: 'white',
                          '& .MuiChip-label': {
                            color: 'white',
                          },
                        }}
                      />
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Display Order
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SortIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {leadStage.order || 'Not set'}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {leadStage.createdAt ? formatDateTime(leadStage.createdAt) : 'Unknown'}
                  </Typography>
                </Box>
                {leadStage.updatedAt && leadStage.updatedAt !== leadStage.createdAt && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(leadStage.updatedAt)}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Stage Timeline
              </Typography>
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <TrendingUpIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Lead Stage Created
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {leadStage.createdAt ? formatDateTime(leadStage.createdAt) : 'Unknown'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {leadStage.updatedAt && leadStage.updatedAt !== leadStage.createdAt && (
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                        <EditIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Lead Stage Updated
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(leadStage.updatedAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Usage Information
              </Typography>
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Stage Configuration
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Name:
                        {' '}
                        {leadStage.name}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Color:
                        {' '}
                        {leadStage.color}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Order:
                        {' '}
                        {leadStage.order || 'Not set'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {leadStage.description || 'No description available for this lead stage.'}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Visual Preview
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={leadStage.name}
                      color={leadStage.color}
                      sx={{
                        backgroundColor: colorMap[leadStage.color] || colorMap.default,
                        color: 'white',
                        '& .MuiChip-label': {
                          color: 'white',
                        },
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      This is how the stage will appear in the CRM
                    </Typography>
                  </Box>
                </Paper>
              </Stack>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default LeadStageDetail;
